import React, { createContext, useContext, useEffect, useReducer, useState } from 'react';
import { cloneDeep, omit } from 'lodash';
import moment from 'moment';
import uuid from 'uuid';

import { useAuth } from 'contexts/auth';
import { useSnackbar } from 'contexts/snackbar';
import { useUser2 } from 'contexts/user2';
import { useWebsocket } from 'contexts/websocket';
import feedbackService from 'services/feedback';
import groupService from 'services/group';
import taskService from 'services/tasks';
import { DEFAULT_SUBTASK_TITLE, TYPES } from 'utils/constants';
import { debounceRequest } from 'utils/helper';

const TaskContext = createContext();
const useTask = () => useContext(TaskContext);

const initialTaskList = {
    all: {},
    toDo: [],
    inProgress: [],
    underReview: [],
    completed: [],
    modifiedOn: 0,
};
const initialSubtaskList = {};
const initialFeedbackList = {};
const initialFileList = {};
const initialSharedUrlList = {};

const taskListReducer = (state, action) => {
    const { type, payload } = action;

    switch (type) {
        case 'RESET': {
            return initialTaskList;
        }
        case 'SET_TASKLIST_STATE': {
            const { state } = payload;
            return state;
        }
        case 'FETCH_TASKS': {
            const { tasks } = payload;
            return {
                ...state,
                all: tasks
                    .filter(({ active }) => active)
                    .reduce((acc, cur) => {
                        return {
                            ...acc,
                            [cur.id]: cur,
                        };
                    }, {}),
            };
        }
        case 'FETCH_TASK_MAP': {
            const { taskMap } = payload;
            const { toDo, inProgress, underReview, completed, modified_on } = taskMap;
            return {
                ...state,
                toDo,
                inProgress,
                underReview,
                completed,
                modifiedOn: modified_on,
            };
        }
        case 'CREATE_TASK': {
            const { task } = payload;
            return {
                ...state,
                all: {
                    ...state.all,
                    [task.id]: task,
                },
                [task.status]: [...state[task.status], task.id],
            };
        }
        case 'UPDATE_TASK': {
            const { id, key, value, timestamp } = payload;
            const task = state.all[id];
            if (!task) return state;
            const currentStatus = task.status;
            const updatedTask = { ...task };
            if (key === 'title') delete updatedTask.isLocal;
            if (key === 'shouldCreateActivityOnBlur') delete updatedTask.shouldCreateActivityOnBlur;
            return {
                ...state,
                all: {
                    ...state.all,
                    [id]: {
                        ...updatedTask,
                        [key]: value,
                        modified_on: timestamp,
                    },
                },
                ...(key === 'status' && value !== currentStatus
                    ? {
                          [currentStatus]: state[currentStatus].filter((x) => x !== id),
                          [value]: [id, ...state[value]],
                      }
                    : {}),
                modifiedOn: key === 'status' ? timestamp : state.modifiedOn,
            };
        }
        case 'DELETE_TASK': {
            const { id } = payload;
            const currentStatus = state.all[id].status;
            return {
                ...state,
                all: {
                    ...state.all,
                    [id]: {
                        ...state.all[id],
                        active: false,
                        modified_on: new Date().getTime(),
                    },
                },
                [currentStatus]: state[currentStatus].filter((x) => x !== id),
            };
        }
        case 'DUPLICATE_TASK': {
            const { task } = payload;
            return {
                ...state,
                all: {
                    ...state.all,
                    [task.id]: { ...task },
                },
                [task.status]: [...state[task.status], task.id],
            };
        }
        case 'MOVE_TASK': {
            const { id, destination, timestamp } = payload;
            const task = state.all[id];
            if (!task) return state;

            let taskMap = {
                toDo: [...state.toDo],
                inProgress: [...state.inProgress],
                underReview: [...state.underReview],
                completed: [...state.completed],
            };
            const isReorder = task.status === destination.droppableId;

            taskMap[task.status] = taskMap[task.status].filter((item) => item !== id);
            if (isReorder) {
                taskMap[task.status].splice(destination.index, 0, id);
            } else {
                taskMap[destination.droppableId].splice(destination.index, 0, id);
            }
            taskMap.modifiedOn = timestamp;

            return {
                ...state,
                all: {
                    ...state.all,
                    [id]: { ...task, status: destination.droppableId, modified_on: timestamp },
                },
                ...taskMap,
            };
        }
        case 'CLEANUP_LOCAL_TASKS': {
            const { ids } = payload;
            const newState = cloneDeep(state);
            ids.forEach((id) => {
                delete newState.all[id];
            });
            return {
                ...newState,
                toDo: newState.toDo.filter((toDoId) => !ids.includes(toDoId)),
                inProgress: newState.inProgress.filter((inProgressId) => !ids.includes(inProgressId)),
                underReview: newState.underReview.filter((underReviewId) => !ids.includes(underReviewId)),
                completed: newState.completed.filter((completedId) => !ids.includes(completedId)),
            };
        }
        default: {
            return state;
        }
    }
};

const subtaskListReducer = (state, action) => {
    const { type, payload } = action;

    switch (type) {
        case 'RESET': {
            return initialSubtaskList;
        }
        case 'SET_SUBTASKLIST_STATE': {
            const { state } = payload;
            return state;
        }
        case 'FETCH_SUBTASKS': {
            const { subtasks } = payload;
            return subtasks.reduce((acc, cur) => {
                return {
                    ...acc,
                    [cur.id]: cur,
                };
            }, {});
        }
        case 'CREATE_SUBTASK': {
            const { subtask } = payload;
            return {
                ...state,
                [subtask.id]: subtask,
            };
        }
        case 'UPDATE_SUBTASK': {
            const { id, key, value } = payload;
            return {
                ...state,
                [id]: {
                    ...state[id],
                    [key]: value,
                    modified_on: new Date().getTime(),
                },
            };
        }
        case 'DELETE_SUBTASK': {
            const { id } = payload;
            return {
                ...state,
                [id]: {
                    ...state,
                    active: false,
                    modified_on: new Date().getTime(),
                },
            };
        }
        default: {
            return state;
        }
    }
};

const feedbackListReducer = (state, action) => {
    const { type, payload } = action;

    switch (type) {
        case 'RESET': {
            return initialFeedbackList;
        }
        case 'SET_FEEDBACKLIST_STATE': {
            const { state } = payload;
            return state;
        }
        case 'FETCH_FEEDBACK': {
            const { feedback, subfeedback } = payload;
            return feedback.reduce((acc, cur) => {
                return {
                    ...acc,
                    [cur.id]: {
                        ...cur,
                        subfeedback: subfeedback[cur.id]
                            ? subfeedback[cur.id].reduce((_acc, _cur) => {
                                  return {
                                      ..._acc,
                                      [_cur.id]: _cur,
                                  };
                              }, {})
                            : {},
                    },
                };
            }, state);
        }
        case 'ADD_FEEDBACK': {
            const { feedback } = payload;
            return {
                ...state,
                [feedback.id]: {
                    ...feedback,
                    subfeedback: {},
                },
            };
        }
        case 'UPDATE_FEEDBACK': {
            const { id, text } = payload;
            return {
                ...state,
                [id]: {
                    ...state[id],
                    text,
                    modified_on: new Date().getTime(),
                },
            };
        }
        case 'DELETE_FEEDBACK': {
            const { id } = payload;
            return {
                ...state,
                [id]: {
                    ...state[id],
                    active: false,
                },
            };
        }
        case 'ADD_SUBFEEDBACK': {
            const { subfeedback } = payload;
            const { id, parent_id } = subfeedback;
            return {
                ...state,
                [parent_id]: {
                    ...state[parent_id],
                    reply_num: state[parent_id].reply_num + 1,
                    subfeedback: {
                        ...state[parent_id].subfeedback,
                        [id]: subfeedback,
                    },
                },
            };
        }
        case 'UPDATE_SUBFEEDBACK_ID': {
            const { oldId, subfeedback } = payload;
            const { id: newId, parent_id } = subfeedback;
            const subfeedbackParentCopy = cloneDeep(state[parent_id]);
            subfeedbackParentCopy.subfeedback[newId] = subfeedback;
            delete subfeedbackParentCopy.subfeedback[oldId];
            return { ...state, [parent_id]: subfeedbackParentCopy };
        }
        case 'UPDATE_SUBFEEDBACK': {
            const { id, parent_id, text } = payload;
            return {
                ...state,
                [parent_id]: {
                    ...state[parent_id],
                    subfeedback: {
                        ...state[parent_id].subfeedback,
                        [id]: {
                            ...state[parent_id].subfeedback[id],
                            text,
                            modified_on: new Date().getTime(),
                        },
                    },
                },
            };
        }
        case 'DELETE_SUBFEEDBACK': {
            const { id, parent_id } = payload;
            return {
                ...state,
                [parent_id]: {
                    ...state[parent_id],
                    subfeedback: {
                        ...state[parent_id].subfeedback,
                        [id]: {
                            ...state[parent_id].subfeedback[id],
                            active: false,
                        },
                    },
                },
            };
        }
    }
};

const fileListReducer = (state, action) => {
    const { type, payload } = action;

    switch (type) {
        case 'RESET': {
            return initialFileList;
        }
        case 'SET_FILELIST_STATE': {
            const { state } = payload;
            return state;
        }
        case 'FETCH_FILES': {
            return payload.reduce((acc, cur) => {
                return {
                    ...acc,
                    [cur.id]: cur,
                };
            }, state);
        }
        case 'ADD_FILE': {
            const { file } = payload;
            return {
                ...state,
                [file.id]: file,
            };
        }
        case 'DELETE_FILE': {
            const { id } = payload;
            return {
                ...state,
                [id]: {
                    ...state[id],
                    active: false,
                },
            };
        }
    }
};

const sharedUrlListReducer = (state, action) => {
    const { type, payload } = action;

    switch (type) {
        case 'RESET': {
            return initialSharedUrlList;
        }
        case 'SET_SHAREDURLLIST_STATE': {
            const { state } = payload;
            return state;
        }
        case 'FETCH_SHAREDURLS': {
            return payload.reduce((acc, cur) => {
                return {
                    ...acc,
                    [cur.id]: cur,
                };
            }, state);
        }
        case 'ADD_SHAREDURL': {
            const { sharedUrl } = payload;
            return {
                ...state,
                [sharedUrl.id]: sharedUrl,
            };
        }
        case 'DELETE_SHAREDURL': {
            const { id } = payload;
            return {
                ...state,
                [id]: {
                    ...state[id],
                    active: false,
                },
            };
        }
        default:
            return state;
    }
};

const TaskProvider = ({ ...props }) => {
    const { auth } = useAuth();
    const { message } = useSnackbar();
    const { currentGroup } = useUser2();
    const { sendOn, taskWS, setTaskWS } = useWebsocket();

    const [taskList, dispatchTaskAction] = useReducer(taskListReducer, initialTaskList);
    const [subtaskList, dispatchSubtaskAction] = useReducer(subtaskListReducer, initialSubtaskList);
    const [feedbackList, dispatchFeedbackAction] = useReducer(feedbackListReducer, initialFeedbackList);
    const [fileList, dispatchFileAction] = useReducer(fileListReducer, initialFileList);
    const [sharedUrlList, dispatchSharedUrlAction] = useReducer(sharedUrlListReducer, initialSharedUrlList);

    const [isLoading, setIsLoading] = useState(true);
    const [isTaskDrawerOpen, setIsTaskDrawerOpen] = useState(false);
    const [taskDrawerContent, setTaskDrawerContent] = useState({ task: {} });
    const [aiFeedback, setAIFeedback] = useState(null);
    const [aiFeedbackOverlay, setAIFeedbackOverlay] = useState({ open: false, aiFeedback: null });
    const [filterSort, setFilterSort] = useState({
        sortKey: 'none',
        workloadFilterKey: [],
        assigneeFilterKey: [],
        dueFilterKey: [],
        searchBarInput: '',
    });
    const [alertDialog, setAlertDialog] = useState({
        visible: false,
        title: '',
        content: '',
        actionButtonText: 'Delete',
        variant: 'destructive',
        onConfirm: () => {},
        onCancel: () => {},
    });

    const resetAlertDialog = () => {
        setAlertDialog((current) => ({
            ...current,
            visible: false,
            onConfirm: () => {},
            onCancel: () => {},
        }));
        setTimeout(() => {
            setAlertDialog((current) => ({
                ...current,
                title: '',
                content: '',
                actionButtonText: '',
            }));
        }, 100);
    };

    const reset = () => {
        if (auth.role === 'instructor') {
            dispatchTaskAction({ type: 'RESET' });
            dispatchSubtaskAction({ type: 'RESET' });
            dispatchFeedbackAction({ type: 'RESET' });
            dispatchFileAction({ type: 'RESET' });
            dispatchSharedUrlAction({ type: 'RESET' });
        }
        setFilterSort({
            sortKey: 'none',
            workloadFilterKey: [],
            assigneeFilterKey: [],
            dueFilterKey: [],
            searchBarInput: '',
        });
    };

    const cleanupLocalTasks = (ids) => {
        dispatchTaskAction({
            type: 'CLEANUP_LOCAL_TASKS',
            payload: { ids },
        });
    };

    const getSort = () => {
        switch (filterSort.sortKey) {
            default:
            case 'none':
                return () => 0;
            case 'closestdue':
                return (taskA, taskB) => taskList.all[taskA].end_on - taskList.all[taskB].end_on;
            case 'furthestdue':
                return (taskA, taskB) => taskList.all[taskB].end_on - taskList.all[taskA].end_on;
            case 'titlea-z':
                return (taskA, taskB) =>
                    taskList.all[taskA].title.toLowerCase() > taskList.all[taskB].title.toLowerCase() ? 1 : -1;
            case 'titlez-a':
                return (taskA, taskB) =>
                    taskList.all[taskA].title.toLowerCase() < taskList.all[taskB].title.toLowerCase() ? 1 : -1;
        }
    };

    const checkPassDueFilter = (dueDate, dueFilter) => {
        const today = moment(new Date());
        today.hour(0);
        today.minute(0);
        today.second(0);
        today.millisecond(0);

        if (dueFilter.includes('today') && dueDate.format('D M YYYY') === today.format('D M YYYY')) {
            return true;
        }
        if (dueFilter.includes('thisweek')) {
            const startOfWeek = today.clone().day(0).add(1, 'day');
            const endOfWeek = today.clone().day(6).add(1, 'day');
            if (dueDate.isSameOrAfter(startOfWeek) && dueDate.isSameOrBefore(endOfWeek)) {
                return true;
            }
        }
        if (dueFilter.includes('nextweek')) {
            const startOfNextWeek = today.clone().add(7, 'days').day(0).add(1, 'day');
            const endOfNextWeek = today.clone().add(7, 'days').day(6).add(1, 'day');
            if (dueDate.isSameOrAfter(startOfNextWeek) && dueDate.isSameOrBefore(endOfNextWeek)) {
                return true;
            }
        }
        if (dueFilter.includes('overdue') && dueDate.toDate() < today.toDate()) {
            return true;
        }

        return false;
    };

    const getTaskById = (id) => {
        return taskList.all[id];
    };

    const getTasks = () => {
        const hasWorkloadFilter = Boolean(filterSort.workloadFilterKey.length);
        const hasAssigneeFilter = Boolean(filterSort.assigneeFilterKey.length);
        const hasDueFilter = Boolean(filterSort.dueFilterKey.length);

        return ['toDo', 'inProgress', 'underReview', 'completed'].reduce((acc, column) => {
            return {
                ...acc,
                [column]: [...taskList[column]].sort(getSort()).map((id) => {
                    const task = taskList.all[id];
                    const passesWorkloadFilter = hasWorkloadFilter
                        ? filterSort.workloadFilterKey.includes(task.workload)
                        : true;
                    const passesAssigneeFilter = hasAssigneeFilter
                        ? filterSort.assigneeFilterKey.includes(task.assignee_account_id)
                        : true;
                    const passesDueFilter = hasDueFilter
                        ? checkPassDueFilter(moment(task.end_on), filterSort.dueFilterKey)
                        : true;
                    const passesSearchBarInput = filterSort.searchBarInput
                        ? task.title?.toLowerCase().includes(filterSort.searchBarInput.toLowerCase())
                        : true;

                    return {
                        ...task,
                        column,
                        show: passesWorkloadFilter && passesAssigneeFilter && passesDueFilter && passesSearchBarInput,
                    };
                }),
            };
        }, {});
    };

    const createTask = (status, milestoneId) => {
        const id = uuid.v4();
        const createdTime = new Date().getTime();
        const newTask = {
            id,
            active: true,
            owner_account_id: auth.owner_account_id,
            course_id: auth.course_id,
            group_id: currentGroup.id,
            title: '',
            workload: 'low',
            status,
            created_on: createdTime,
            modified_on: createdTime,
            end_on: createdTime,
            feedback_num: 0,
            file_num: 0,
            isLocal: true,
            shouldCreateActivityOnBlur: true,
            ...(milestoneId && {
                milestone_id: milestoneId,
            }),
        };
        dispatchTaskAction({
            type: 'CREATE_TASK',
            payload: { task: newTask },
        });
        return newTask;
    };

    const updateTask = async (id, key, value, shouldFlush) => {
        const timestamp = new Date().getTime();
        dispatchTaskAction({
            type: 'UPDATE_TASK',
            payload: { id, key, value, timestamp },
        });

        debounceRequest(() => {
            if (key === 'title') {
                sendOn('default', {
                    payload_type: 'upsert_task',
                    payload: {
                        task: omit({ ...getTaskById(id), title: value }, [
                            '_id',
                            'isLocal',
                            'shouldCreateActivityOnBlur',
                        ]),
                        group_id: currentGroup.id,
                        timestamp,
                    },
                });
            } else if (key !== 'shouldCreateActivityOnBlur') {
                sendOn('default', {
                    payload_type: 'update_task',
                    payload: {
                        task_id: id,
                        group_id: currentGroup.id,
                        key,
                        old_value: getTaskById(id)[key],
                        new_value: value,
                        timestamp,
                    },
                });
            }
        });

        if (shouldFlush) debounceRequest.flush();

        if (key === 'status' && value === 'completed') {
            const subtasks = getSubtasksByTaskId(id);
            subtasks.forEach((subtask) => {
                updateSubtask(subtask.id, 'status', 'completed');
            });
            if (subtasks.length) {
                message.success('Subtasks were completed automatically.');
            }
        }

        if (getTaskById(id).type === TYPES.MILESTONE) {
            if (key === 'status' && value === 'completed') {
                const linkedTasks = Object.values(taskList.all).filter(
                    (task) => task.type !== TYPES.MILESTONE && task.milestone_id === getTaskById(id).milestone_id
                );
                linkedTasks.forEach((linkedTask) => {
                    updateTask(linkedTask.id, 'status', 'completed', true);
                });
                if (linkedTasks.length) {
                    message.success('Linked tasks were completed automatically.');
                }
            }
        }
    };

    const deleteTask = async (id) => {
        if (getTaskById(id)?.isLocal) return;

        dispatchTaskAction({
            type: 'DELETE_TASK',
            payload: { id },
        });

        sendOn('default', {
            payload_type: 'delete_task',
            payload: {
                task_id: id,
                group_id: currentGroup.id,
                status: getTaskById(id).status,
            },
        });

        const subtasks = getSubtasksByTaskId(id);
        subtasks.forEach((subtask) => {
            deleteSubtask(subtask.id);
        });
    };

    const duplicateTask = (id) => {
        const dupId = uuid.v4();
        const createdTime = new Date().getTime();
        const dupTask = {
            ...taskList.all[id],
            id: dupId,
            owner_account_id: auth.owner_account_id,
            created_on: createdTime,
            modified_on: createdTime,
            file_num: 0,
            feedback_num: 0,
        };
        delete dupTask._id;
        delete dupTask.isLocal;
        delete dupTask.shouldCreateActivityOnBlur;
        delete dupTask.type;
        delete dupTask.template_id;
        delete dupTask.published_on;
        delete dupTask.milestone_id;
        delete dupTask.submission_type;
        dispatchTaskAction({
            type: 'DUPLICATE_TASK',
            payload: { task: dupTask },
        });

        sendOn('default', {
            payload_type: 'create_task',
            payload: {
                task: dupTask,
                group_id: currentGroup.id,
            },
        });

        return dupTask;
    };

    const moveTask = (id, source, destination) => {
        if (!destination) return;

        if (filterSort.sortKey !== 'none') {
            if (source.droppableId === destination.droppableId) return;
            destination.index = 0;
        }

        const timestamp = new Date().getTime();
        dispatchTaskAction({
            type: 'MOVE_TASK',
            payload: { id, destination, timestamp },
        });

        sendOn('default', {
            payload_type: 'move_task',
            payload: { task_id: id, group_id: currentGroup.id, destination, timestamp },
        });

        if (destination.droppableId === 'completed') {
            const subtasks = getSubtasksByTaskId(id);
            subtasks.forEach((subtask) => {
                updateSubtask(subtask.id, 'status', 'completed');
            });
            if (subtasks.length) {
                message.success('Subtasks were completed automatically.');
            }
        }
    };

    const getSubtasksByTaskId = (taskId) => {
        return Object.entries(subtaskList)
            .filter(([, subtask]) => {
                return subtask.active && subtask.parent_id === taskId;
            })
            .map(([id, subtask]) => {
                return subtask;
            });
    };

    const getSubtaskById = (subtaskId) => {
        return subtaskList[subtaskId];
    };

    const createSubtask = (parentId) => {
        const id = uuid.v4();
        const createdTime = new Date().getTime();
        const newSubtask = {
            id,
            active: true,
            owner_account_id: auth.owner_account_id,
            course_id: auth.course_id,
            group_id: currentGroup.id,
            title: DEFAULT_SUBTASK_TITLE,
            workload: 'low',
            status: 'toDo',
            parent_id: parentId,
            created_on: createdTime,
            modified_on: createdTime,
            end_on: createdTime,
        };
        dispatchSubtaskAction({
            type: 'CREATE_SUBTASK',
            payload: { subtask: newSubtask },
        });

        sendOn('default', {
            payload_type: 'create_subtask',
            payload: {
                subtask: newSubtask,
                group_id: currentGroup.id,
            },
        });

        return { ...newSubtask, id };
    };

    const updateSubtask = async (id, key, value) => {
        dispatchSubtaskAction({
            type: 'UPDATE_SUBTASK',
            payload: { id, key, value },
        });

        if (key === 'title') {
            debounceRequest(() => {
                sendOn('default', {
                    payload_type: 'update_subtask',
                    payload: {
                        subtask_id: id,
                        group_id: currentGroup.id,
                        key,
                        value,
                    },
                });
            });
        } else {
            sendOn('default', {
                payload_type: 'update_subtask',
                payload: {
                    subtask_id: id,
                    group_id: currentGroup.id,
                    key,
                    value,
                },
            });
        }
    };

    const deleteSubtask = async (id) => {
        dispatchSubtaskAction({
            type: 'DELETE_SUBTASK',
            payload: { id },
        });

        sendOn('default', {
            payload_type: 'delete_subtask',
            payload: {
                subtask_id: id,
                group_id: currentGroup.id,
            },
        });
    };

    const countSubtasks = (id) => {
        return Object.values(subtaskList).filter((subtask) => {
            return subtask.active && subtask.parent_id === id;
        }).length;
    };

    const fetchFeedbackByTaskId = async (taskId) => {
        const { feedback = [], subfeedback = {} } = await taskService.listTaskFeedbacks(taskId);
        const files = await taskService.findTaskFiles(taskId);
        dispatchFeedbackAction({ type: 'FETCH_FEEDBACK', payload: { feedback, subfeedback } });
        dispatchFileAction({ type: 'FETCH_FILES', payload: files });
    };

    const getFeedbackByTaskId = (taskId) => {
        return Object.values(feedbackList)
            .filter((feedback) => {
                return feedback.parent_id === taskId;
            })
            .reduce((acc, cur) => {
                return {
                    ...acc,
                    [cur.id]: cur,
                };
            }, {});
    };

    const addFeedback = async (taskId, text, files) => {
        const feedbackState = cloneDeep(feedbackList);
        const taskState = cloneDeep(taskList);
        const createdTime = new Date().getTime();
        const id = uuid.v4();
        const newFeedback = {
            id,
            active: true,
            owner_account_id: auth.owner_account_id,
            parent_id: taskId,
            grand_parent_id: currentGroup.id,
            text,
            type: TYPES.TASK,
            created_on: createdTime,
            modified_on: createdTime,
            reply_num: 0,
        };
        dispatchFeedbackAction({
            type: 'ADD_FEEDBACK',
            payload: { feedback: newFeedback },
        });
        dispatchTaskAction({
            type: 'UPDATE_TASK',
            payload: {
                id: taskId,
                key: 'feedback_num',
                value: getTaskById(taskId).feedback_num + 1,
            },
        });

        sendOn('default', {
            payload_type: 'update_task',
            payload: {
                task_id: taskId,
                group_id: currentGroup.id,
                key: 'feedback_num',
                old_value: getTaskById(taskId).feedback_num,
                new_value: getTaskById(taskId).feedback_num + 1,
            },
        });

        try {
            await taskService.createTaskFeedback(taskId, newFeedback);
            if (files.length) await addFileFeedback(taskId, id, files);
        } catch (e) {
            console.error(e);
            dispatchFeedbackAction({
                type: 'SET_FEEDBACKLIST_STATE',
                payload: { state: feedbackState },
            });
            dispatchTaskAction({
                type: 'SET_TASKLIST_STATE',
                payload: { state: taskState },
            });
        }
    };

    const updateFeedback = async (id, text) => {
        const currentState = cloneDeep(feedbackList);
        dispatchFeedbackAction({ type: 'UPDATE_FEEDBACK', payload: { id, text } });

        try {
            await feedbackService.updateFeedback(id, { text, modified_on: new Date().getTime() });
        } catch (e) {
            console.error(e);
            dispatchFeedbackAction({
                type: 'SET_FEEDBACKLIST_STATE',
                payload: { state: currentState },
            });
        }
    };

    const deleteFeedback = async (id) => {
        const feedbackState = cloneDeep(feedbackList);
        const taskState = cloneDeep(taskList);
        const taskId = feedbackList[id].parent_id;
        dispatchFeedbackAction({ type: 'DELETE_FEEDBACK', payload: { id } });
        dispatchTaskAction({
            type: 'UPDATE_TASK',
            payload: {
                id: taskId,
                key: 'feedback_num',
                value: getTaskById(taskId).feedback_num - 1,
            },
        });

        sendOn('default', {
            payload_type: 'update_task',
            payload: {
                task_id: taskId,
                group_id: currentGroup.id,
                key: 'feedback_num',
                old_value: getTaskById(taskId).feedback_num,
                new_value: getTaskById(taskId).feedback_num - 1,
            },
        });

        try {
            await feedbackService.deleteFeedback(id, { task_id: taskId });
        } catch (e) {
            console.error(e);
            dispatchFeedbackAction({
                type: 'SET_FEEDBACKLIST_STATE',
                payload: { state: feedbackState },
            });
            dispatchFeedbackAction({
                type: 'SET_TASKLIST_STATE',
                payload: { state: taskState },
            });
        }
    };

    const addSubfeedback = async (taskId, parentId, text, files) => {
        const feedbackState = cloneDeep(feedbackList);
        const taskState = cloneDeep(taskList);
        const createdTime = new Date().getTime();
        const id = uuid.v4();
        const newSubfeedback = {
            id,
            active: true,
            owner_account_id: auth.owner_account_id,
            parent_id: parentId,
            grand_parent_id: taskId,
            text,
            type: TYPES.FEEDBACK,
            created_on: createdTime,
            modified_on: createdTime,
            reply_num: 0,
        };
        dispatchFeedbackAction({
            type: 'ADD_SUBFEEDBACK',
            payload: { subfeedback: newSubfeedback },
        });
        dispatchTaskAction({
            type: 'UPDATE_TASK',
            payload: {
                id: taskId,
                key: 'feedback_num',
                value: getTaskById(taskId).feedback_num + 1,
            },
        });

        sendOn('default', {
            payload_type: 'update_task',
            payload: {
                task_id: taskId,
                group_id: currentGroup.id,
                key: 'feedback_num',
                old_value: getTaskById(taskId).feedback_num,
                new_value: getTaskById(taskId).feedback_num + 1,
            },
        });

        try {
            await taskService.createTaskFeedback(taskId, newSubfeedback);
            if (files.length) await addFileFeedback(taskId, id, files);
        } catch (e) {
            console.error(e);
            dispatchFeedbackAction({
                type: 'SET_FEEDBACKLIST_STATE',
                payload: { state: feedbackState },
            });
            dispatchFeedbackAction({
                type: 'SET_TASKLIST_STATE',
                payload: { state: taskState },
            });
        }
    };

    const updateSubfeedback = async (id, parentId, text) => {
        const currentState = cloneDeep(feedbackList);
        dispatchFeedbackAction({
            type: 'UPDATE_SUBFEEDBACK',
            payload: { id, parent_id: parentId, text },
        });

        try {
            await feedbackService.updateFeedback(id, { text, modified_on: new Date().getTime() });
        } catch (e) {
            console.error(e);
            dispatchFeedbackAction({
                type: 'SET_FEEDBACKLIST_STATE',
                payload: { state: currentState },
            });
        }
    };

    const deleteSubfeedback = async (id, parentId) => {
        const feedbackState = cloneDeep(feedbackList);
        const taskState = cloneDeep(taskList);
        const taskId = feedbackList[parentId].parent_id;
        dispatchFeedbackAction({
            type: 'DELETE_SUBFEEDBACK',
            payload: { id, parent_id: parentId },
        });
        dispatchTaskAction({
            type: 'UPDATE_TASK',
            payload: {
                id: taskId,
                key: 'feedback_num',
                value: getTaskById(taskId).feedback_num - 1,
            },
        });

        sendOn('default', {
            payload_type: 'update_task',
            payload: {
                task_id: taskId,
                group_id: currentGroup.id,
                key: 'feedback_num',
                old_value: getTaskById(taskId).feedback_num,
                new_value: getTaskById(taskId).feedback_num - 1,
            },
        });

        try {
            await feedbackService.deleteFeedback(id, { task_id: feedbackList[parentId].parent_id });
        } catch (e) {
            console.error(e);
            dispatchFeedbackAction({
                type: 'SET_FEEDBACKLIST_STATE',
                payload: { state: feedbackState },
            });
            dispatchFeedbackAction({
                type: 'SET_TASKLIST_STATE',
                payload: { state: taskState },
            });
        }
    };

    const countFeedback = (taskId) => {
        if (!taskId) return 0;
        return getTaskById(taskId)?.feedback_num || 0;
    };

    const addFileFeedback = async (taskId, parentId, files) => {
        if (!files.length) return;
        const fileState = cloneDeep(fileList);
        const taskState = cloneDeep(taskList);
        const createdTime = new Date().getTime();
        const file = files[0];
        const id = uuid.v4();
        const newFile = {
            id,
            active: true,
            owner_account_id: auth.owner_account_id,
            grand_parent_id: taskId,
            parent_id: parentId,
            data: {
                bucket: file.bucket,
                key: file.key,
                name: file.name,
                type: file.type,
                size: file.size,
            },
            type: TYPES.FEEDBACK,
            public: true,
            created_on: createdTime,
            modified_on: createdTime,
            course_id: auth.course_id,
            group_id: currentGroup.id,
        };
        dispatchFileAction({
            type: 'ADD_FILE',
            payload: { file: newFile },
        });
        dispatchTaskAction({
            type: 'UPDATE_TASK',
            payload: {
                id: taskId,
                key: 'file_num',
                value: getTaskById(taskId).file_num + 1,
            },
        });

        sendOn('default', {
            payload_type: 'update_task',
            payload: {
                task_id: taskId,
                group_id: currentGroup.id,
                key: 'file_num',
                old_value: getTaskById(taskId).file_num,
                new_value: getTaskById(taskId).file_num + 1,
            },
        });

        try {
            await feedbackService.createFeedbackFiles(parentId, newFile);
        } catch (e) {
            console.error(e);
            dispatchFileAction({
                type: 'SET_FILELIST_STATE',
                payload: { state: fileState },
            });
            dispatchFileAction({
                type: 'SET_TASKLIST_STATE',
                payload: { state: taskState },
            });
        }
    };

    const deleteFile = async (id) => {
        const fileState = cloneDeep(fileList);
        const taskState = cloneDeep(taskList);
        const taskId = fileList[id].grand_parent_id;
        dispatchFileAction({ type: 'DELETE_FILE', payload: { id } });
        dispatchTaskAction({
            type: 'UPDATE_TASK',
            payload: {
                id: taskId,
                key: 'file_num',
                value: getTaskById(taskId).file_num - 1,
            },
        });

        sendOn('default', {
            payload_type: 'update_task',
            payload: {
                task_id: taskId,
                group_id: currentGroup.id,
                key: 'file_num',
                old_value: getTaskById(taskId).file_num,
                new_value: getTaskById(taskId).file_num - 1,
            },
        });

        try {
            await groupService.deleteGroupFiles(currentGroup.id, {
                files: [{ id, type: TYPES.FEEDBACK, grand_parent_id: fileList[id].grand_parent_id }],
            });
        } catch (e) {
            console.error(e);
            dispatchFileAction({
                type: 'SET_FILELIST_STATE',
                payload: { state: fileState },
            });
            dispatchFileAction({
                type: 'SET_TASKLIST_STATE',
                payload: { state: taskState },
            });
        }
    };

    const countFiles = (taskId) => {
        if (!taskId) return 0;
        return getTaskById(taskId)?.file_num || 0;
    };

    const fetchSharedUrlsByTaskId = async (taskId) => {
        const sharedUrls = await taskService.findTaskSharedUrls(taskId);
        dispatchSharedUrlAction({ type: 'FETCH_SHAREDURLS', payload: sharedUrls });
    };

    const createTaskResource = (taskId, resource) => {
        if (resource.data) {
            dispatchFileAction({
                type: 'ADD_FILE',
                payload: {
                    file: {
                        ...resource,
                        active: true,
                        created_on: new Date().getTime(),
                        modified_on: new Date().getTime(),
                        public: true,
                        type: TYPES.TASK,
                        task_id: taskId,
                    },
                },
            });
        } else {
            dispatchSharedUrlAction({
                type: 'ADD_SHAREDURL',
                payload: {
                    sharedUrl: {
                        ...resource,
                        active: true,
                        created_on: new Date().getTime(),
                        modified_on: new Date().getTime(),
                        public: true,
                        type: TYPES.TASK,
                        task_id: taskId,
                    },
                },
            });
        }
        sendOn('default', {
            payload_type: 'create_task_resource',
            payload: { course_id: auth.course_id, group_id: currentGroup.id, task_id: taskId, resource },
        });
    };

    const deleteTaskResource = (resourceType, resourceId) => {
        switch (resourceType) {
            case 'file':
                dispatchFileAction({
                    type: 'DELETE_FILE',
                    payload: { id: resourceId },
                });
                break;
            case 'link':
                dispatchSharedUrlAction({
                    type: 'DELETE_SHAREDURL',
                    payload: { id: resourceId },
                });
                break;
            default:
                break;
        }
        sendOn('default', {
            payload_type: 'delete_task_resource',
            payload: { group_id: currentGroup.id, resource_id: resourceId, resource_type: resourceType },
        });
    };

    const createActivity = async (data, type, action) => {
        await groupService.createGroupActivities(currentGroup.id, { data, type, action });
    };

    const setSort = (key) => {
        return setFilterSort((prev) => ({
            ...prev,
            sortKey: key,
        }));
    };

    const toggleWorkloadFilter = (key) => {
        return setFilterSort((prev) => {
            return prev.workloadFilterKey.includes(key)
                ? {
                      ...prev,
                      workloadFilterKey: prev.workloadFilterKey.filter((k) => k !== key),
                  }
                : {
                      ...prev,
                      workloadFilterKey: [...prev.workloadFilterKey, key],
                  };
        });
    };

    const toggleAssigneeFilter = (key) => {
        return setFilterSort((prev) => {
            return prev.assigneeFilterKey.includes(key)
                ? {
                      ...prev,
                      assigneeFilterKey: prev.assigneeFilterKey.filter((k) => k !== key),
                  }
                : {
                      ...prev,
                      assigneeFilterKey: [...prev.assigneeFilterKey, key],
                  };
        });
    };

    const toggleDueFilter = (key) => {
        return setFilterSort((prev) => {
            return prev.dueFilterKey.includes(key)
                ? {
                      ...prev,
                      dueFilterKey: prev.dueFilterKey.filter((k) => k !== key),
                  }
                : {
                      ...prev,
                      dueFilterKey: [...prev.dueFilterKey, key],
                  };
        });
    };

    const onChangeSearchBarInput = (value) => {
        return setFilterSort((prev) => ({
            ...prev,
            searchBarInput: value,
        }));
    };

    const resetFilterSort = () => {
        return setFilterSort((prev) => ({
            ...prev,
            sortKey: 'none',
            workloadFilterKey: [],
            assigneeFilterKey: [],
            dueFilterKey: [],
        }));
    };

    const getCurrentUserCreatedTasks = () => {
        return Object.values(taskList.all)
            .filter(({ active }) => active)
            .filter(({ owner_account_id }) => owner_account_id === auth.owner_account_id);
    };

    const getCurrentUserAssignedTasks = () => {
        return getUserAssignedTasks(auth.owner_account_id);
    };

    const getUserAssignedTasks = (accountId) => {
        return Object.values(taskList.all)
            .filter(({ active }) => active)
            .filter(({ assignee_account_id }) => {
                return accountId ? assignee_account_id === accountId : !assignee_account_id;
            });
    };

    const getUserAssignedSubtasks = (accountId) => {
        return Object.values(subtaskList)
            .filter(({ active }) => active)
            .filter(({ assignee_account_id }) => {
                return accountId ? assignee_account_id === accountId : !assignee_account_id;
            });
    };

    const getUserCollaboratorTasks = (accountId) => {
        return Object.values(taskList.all)
            .filter(({ active }) => active)
            .filter(({ collaborator_account_ids }) => {
                return accountId
                    ? (collaborator_account_ids || []).includes(accountId)
                    : !collaborator_account_ids?.length;
            });
    };

    const getUserCollaboratorSubtasks = (accountId) => {
        return Object.values(subtaskList)
            .filter(({ active }) => active)
            .filter(({ collaborator_account_ids }) => {
                return accountId
                    ? (collaborator_account_ids || []).includes(accountId)
                    : !collaborator_account_ids?.length;
            });
    };

    const getOverdueTasks = () => {
        return Object.values(taskList.all)
            .filter(({ active }) => active)
            .filter(({ status }) => status !== 'completed')
            .filter(({ end_on }) => {
                return (
                    end_on < new Date().getTime() &&
                    moment(end_on).format('D M YYYY') !== moment(new Date()).format('D M YYYY')
                );
            });
    };

    useEffect(() => {
        if (!isTaskDrawerOpen) {
            const localTaskIds = Object.values(taskList.all)
                .filter((task) => task.isLocal)
                .map(({ id }) => id);
            cleanupLocalTasks(localTaskIds);
        }
    }, [isTaskDrawerOpen]);

    useEffect(() => {
        if (!taskWS || !currentGroup?.id) return;
        const { is_sender, payload_type, payload } = taskWS;
        switch (payload_type) {
            case 'create_task': {
                if (is_sender) return;
                const { task } = payload;
                dispatchTaskAction({
                    type: 'CREATE_TASK',
                    payload: { task },
                });
                break;
            }
            case 'upsert_task': {
                if (is_sender) return;
                const { task } = payload;
                if (!getTaskById(task.id)) {
                    dispatchTaskAction({
                        type: 'CREATE_TASK',
                        payload: { task },
                    });
                } else {
                    dispatchTaskAction({
                        type: 'UPDATE_TASK',
                        payload: { id: task.id, key: 'title', value: task.title },
                    });
                }
            }
            case 'update_task': {
                const { task_id, key, new_value, timestamp } = payload;
                if (is_sender) return;
                if (key === 'status' && timestamp < taskList.modifiedOn) {
                    groupService
                        .findGroupTaskMap(currentGroup.id)
                        .then((currentTaskMap) => {
                            dispatchTaskAction({
                                type: 'FETCH_TASK_MAP',
                                payload: { taskMap: currentTaskMap },
                            });
                        })
                        .catch(console.error);
                } else {
                    dispatchTaskAction({
                        type: 'UPDATE_TASK',
                        payload: { id: task_id, key, value: new_value },
                    });
                }
                break;
            }
            case 'delete_task': {
                const { task_id } = payload;
                if (isTaskDrawerOpen && taskDrawerContent?.task?.id === task_id) {
                    setIsTaskDrawerOpen(false);
                    setTaskDrawerContent({ task: {} });
                }
                dispatchTaskAction({
                    type: 'DELETE_TASK',
                    payload: { id: task_id },
                });
                break;
            }
            case 'move_task': {
                const { task_id, destination, timestamp } = payload;
                if (timestamp < taskList.modifiedOn) {
                    groupService
                        .findGroupTaskMap(currentGroup.id)
                        .then((currentTaskMap) => {
                            dispatchTaskAction({
                                type: 'FETCH_TASK_MAP',
                                payload: { taskMap: currentTaskMap },
                            });
                        })
                        .catch(console.error);
                } else {
                    dispatchTaskAction({
                        type: 'MOVE_TASK',
                        payload: { id: task_id, destination, timestamp },
                    });
                }
                break;
            }
            case 'create_subtask': {
                if (is_sender) return;
                const { subtask } = payload;
                dispatchSubtaskAction({
                    type: 'CREATE_SUBTASK',
                    payload: { subtask },
                });
                break;
            }
            case 'update_subtask': {
                if (is_sender) return;
                const { subtask_id, key, value } = payload;
                dispatchSubtaskAction({
                    type: 'UPDATE_SUBTASK',
                    payload: { id: subtask_id, key, value },
                });
                break;
            }
            case 'delete_subtask': {
                const { subtask_id } = payload;
                dispatchSubtaskAction({
                    type: 'DELETE_SUBTASK',
                    payload: { id: subtask_id },
                });
                break;
            }
            case 'update_template': {
                (async () => {
                    const taskResults = await groupService.listGroupTasks(currentGroup.id);
                    const taskMap = await groupService.findGroupTaskMap(currentGroup.id);
                    const activeTasks = taskResults.filter((task) => task.active);
                    const tasks = activeTasks.filter((task) => !task.parent_id);
                    const subtasks = activeTasks.filter((task) => task.parent_id);
                    dispatchTaskAction({
                        type: 'FETCH_TASKS',
                        payload: { tasks },
                    });
                    dispatchSubtaskAction({
                        type: 'FETCH_SUBTASKS',
                        payload: { subtasks },
                    });
                    dispatchTaskAction({
                        type: 'FETCH_TASK_MAP',
                        payload: { taskMap },
                    });
                })();
            }
            case 'create_ai_feedback': {
                if (is_sender) return;
                const { ai_feedback } = payload;
                setAIFeedback((current) => [...current, ai_feedback]);
                break;
            }
            case 'update_ai_feedback': {
                if (is_sender) return;
                const { ai_feedback } = payload;
                setAIFeedback((current) => {
                    return current.map((feedback) => {
                        return feedback.id === ai_feedback.id ? ai_feedback : feedback;
                    });
                });
                break;
            }
            default:
                return;
        }
        setTaskWS();
    }, [taskWS, setTaskWS]);

    useEffect(() => {
        let isCancelled = false;
        if (auth) {
            if (currentGroup?.id) {
                (async () => {
                    setIsLoading(true);
                    const taskResults = await groupService.listGroupTasks(currentGroup.id);
                    const taskMap = await groupService.findGroupTaskMap(currentGroup.id);
                    const aiFeedback = await groupService.listGroupAIFeedback(currentGroup.id);
                    const activeTasks = taskResults.filter((task) => task.active);
                    const tasks = activeTasks.filter((task) => !task.parent_id);
                    const subtasks = activeTasks.filter((task) => task.parent_id);
                    if (!isCancelled) {
                        dispatchTaskAction({
                            type: 'FETCH_TASKS',
                            payload: { tasks },
                        });
                        dispatchSubtaskAction({
                            type: 'FETCH_SUBTASKS',
                            payload: { subtasks },
                        });
                        dispatchTaskAction({
                            type: 'FETCH_TASK_MAP',
                            payload: { taskMap },
                        });
                        setAIFeedback(aiFeedback);
                    }
                    setIsLoading(false);
                })();
            } else return reset;
        }
        return () => {
            isCancelled = true;
        };
    }, [auth, currentGroup?.id]);

    return (
        <TaskContext.Provider
            value={{
                isLoading,
                reset,

                // task
                taskList,
                getTaskById,
                getTasks,
                createTask,
                updateTask,
                deleteTask,
                duplicateTask,
                moveTask,

                // subtask
                subtaskList,
                getSubtasksByTaskId,
                getSubtaskById,
                createSubtask,
                updateSubtask,
                deleteSubtask,
                countSubtasks,

                // feedback
                fetchFeedbackByTaskId,
                getFeedbackByTaskId,
                addFeedback,
                updateFeedback,
                deleteFeedback,
                addSubfeedback,
                updateSubfeedback,
                deleteSubfeedback,
                countFeedback,

                // files
                fileList,
                deleteFile,
                countFiles,

                // resources
                sharedUrlList,
                fetchSharedUrlsByTaskId,
                createTaskResource,
                deleteTaskResource,

                // activities
                createActivity,

                // taskboard drawer
                isTaskDrawerOpen,
                setIsTaskDrawerOpen,
                taskDrawerContent,
                setTaskDrawerContent,

                // taskboard filtering and sorting
                filterSort,
                setSort,
                toggleWorkloadFilter,
                toggleAssigneeFilter,
                toggleDueFilter,
                onChangeSearchBarInput,
                resetFilterSort,

                // ai feedback
                aiFeedback,
                setAIFeedback,
                aiFeedbackOverlay,
                setAIFeedbackOverlay,

                // rest
                getCurrentUserCreatedTasks,
                getCurrentUserAssignedTasks,
                getUserAssignedTasks,
                getUserAssignedSubtasks,
                getUserCollaboratorTasks,
                getUserCollaboratorSubtasks,
                getOverdueTasks,

                // alert dialog
                alertDialog,
                setAlertDialog,
                resetAlertDialog,
            }}
            {...props}
        />
    );
};

export { useTask };
export default TaskProvider;
